import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { db } from './config';

// User profile operations
export const createUserProfile = async (userId, userData) => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      ...userData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error creating user profile:', error);
    return { error: error.message };
  }
};

export const getUserProfile = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return { data: userSnap.data(), id: userSnap.id };
    } else {
      return { data: null };
    }
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return { error: error.message };
  }
};

export const updateUserProfile = async (userId, userData) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...userData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating user profile:', error);
    return { error: error.message };
  }
};

// Order operations
export const createOrder = async (orderData) => {
  try {
    // Add order to orders collection
    const ordersRef = collection(db, 'orders');
    const newOrder = {
      ...orderData,
      updatedAt: serverTimestamp()
    };
    
    const orderDoc = await addDoc(ordersRef, newOrder);
    
    // Also add to user's orders subcollection
    const userOrderRef = doc(db, 'users', orderData.userId, 'orders', orderDoc.id);
    await setDoc(userOrderRef, newOrder);
    
    return { success: true, orderId: orderDoc.id };
  } catch (error) {
    console.error('Error creating order:', error);
    return { error: error.message };
  }
};

export const getUserOrders = async (userId) => {
  try {
    // First try to fetch from the main orders collection without ordering
    const ordersRef = collection(db, 'orders');
    const q = query(
      ordersRef, 
      where('customerInfo.userId', '==', userId)
      // Removed the orderBy to avoid needing a composite index
    );
    
    const querySnapshot = await getDocs(q);
    const orders = [];
    
    querySnapshot.forEach((doc) => {
      orders.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort orders by createdAt in memory instead of in the query
    orders.sort((a, b) => {
      // Handle different timestamp formats
      const getTime = (timestamp) => {
        if (!timestamp) return 0;
        if (timestamp.toMillis) return timestamp.toMillis();
        if (timestamp.seconds) return timestamp.seconds * 1000;
        return new Date(timestamp).getTime();
      };
      
      return getTime(b.createdAt) - getTime(a.createdAt);
    });
    
    // If no orders found in main collection, try the legacy subcollection approach
    if (orders.length === 0) {
      const userOrdersRef = collection(db, 'users', userId, 'orders');
      const userOrdersQuery = query(userOrdersRef);
      
      const userOrdersSnapshot = await getDocs(userOrdersQuery);
      const legacyOrders = [];
      
      userOrdersSnapshot.forEach((doc) => {
        legacyOrders.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort legacy orders by createdAt in memory
      legacyOrders.sort((a, b) => {
        const getTime = (timestamp) => {
          if (!timestamp) return 0;
          if (timestamp.toMillis) return timestamp.toMillis();
          if (timestamp.seconds) return timestamp.seconds * 1000;
          return new Date(timestamp).getTime();
        };
        
        return getTime(b.createdAt) - getTime(a.createdAt);
      });
      
      return { orders: legacyOrders };
    }
    
    return { orders };
  } catch (error) {
    console.error('Error fetching user orders:', error);
    return { error: error.message };
  }
};

export const getOrderDetails = async (orderId) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    
    if (orderSnap.exists()) {
      return { data: orderSnap.data(), id: orderSnap.id };
    } else {
      return { data: null };
    }
  } catch (error) {
    console.error('Error fetching order details:', error);
    return { error: error.message };
  }
};

export const updateOrderStatus = async (orderId, status) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    await updateDoc(orderRef, {
      status,
      updatedAt: serverTimestamp()
    });
    
    // Get the user ID to update in user's subcollection
    const orderSnap = await getDoc(orderRef);
    if (orderSnap.exists()) {
      const { userId } = orderSnap.data();
      const userOrderRef = doc(db, 'users', userId, 'orders', orderId);
      await updateDoc(userOrderRef, {
        status,
        updatedAt: serverTimestamp()
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error updating order status:', error);
    return { error: error.message };
  }
};

// User settings operations
export const updateUserSettings = async (userId, settings) => {
  try {
    const settingsRef = doc(db, 'users', userId, 'settings', 'preferences');
    await setDoc(settingsRef, {
      ...settings,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating user settings:', error);
    return { error: error.message };
  }
};

export const getUserSettings = async (userId) => {
  try {
    const settingsRef = doc(db, 'users', userId, 'settings', 'preferences');
    const settingsSnap = await getDoc(settingsRef);
    
    if (settingsSnap.exists()) {
      return { data: settingsSnap.data() };
    } else {
      return { data: {} };
    }
  } catch (error) {
    console.error('Error fetching user settings:', error);
    return { error: error.message };
  }
};

// Newsletter subscription operations
export const subscribeToNewsletter = async (email) => {
  try {
    // Check if email already exists
    const subscribersRef = collection(db, 'newsletter_subscribers');
    const q = query(subscribersRef, where('email', '==', email.toLowerCase()));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      return { error: 'This email is already subscribed to our newsletter.' };
    }
    
    // Add new subscriber
    await addDoc(subscribersRef, {
      email: email.toLowerCase(),
      subscribedAt: serverTimestamp(),
      status: 'active'
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error subscribing to newsletter:', error);
    return { error: error.message };
  }
};

export const unsubscribeFromNewsletter = async (email) => {
  try {
    const subscribersRef = collection(db, 'newsletter_subscribers');
    const q = query(subscribersRef, where('email', '==', email.toLowerCase()));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { error: 'Email not found in our newsletter list.' };
    }
    
    // Update status to unsubscribed instead of deleting
    const subscriberDoc = querySnapshot.docs[0];
    await updateDoc(doc(db, 'newsletter_subscribers', subscriberDoc.id), {
      status: 'unsubscribed',
      unsubscribedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error unsubscribing from newsletter:', error);
    return { error: error.message };
  }
};
